import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    DeleteOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { DatePicker, Select, Table, Tooltip } from 'antd';
import { FormikProps } from 'formik';
import { observer } from 'mobx-react';
import React from 'react';
import { SquareButton } from '../../../components/action-buttons';
import { Button } from '../../../components/button';
import { ButtonFileInput } from '../../../components/button-file-input';
import { LabeledField } from '../../../components/form-fields/labeled-field';
import { Section } from '../../../components/form-fields/section';
import { TextField } from '../../../components/form-fields/text-field';
import { BusinessUnitsSelectTree } from '../../../components/region-selectors/business-units-select-tree';
import { RichText } from '../../../components/rich-text';
import { Tag } from '../../../components/tag';
import { useContestStore } from '../../../contexts/mobx';
import { cleanProvinceIds } from '../../../utils/rpp-prize-coupon';
import type {
    Contest,
    ContestPolicy,
    ContestPolicyContentType,
    ContestPolicyKind,
    ContestPolicyVersionStatus,
} from '../../../stores/contest';
import { FieldError } from '../general-tab/field-error';
import { Language, LANGUAGES } from '../general-tab/form';
import {
    countParticipants,
    localDate,
    newPolicyForm,
    previewIdOf,
    PoliciesFormValues,
    PolicyForm,
} from './form';
import style from './style.module.scss';

const CONTENT_TYPES: { value: ContestPolicyContentType; label: string }[] = [
    { value: 'html', label: 'Text' },
    { value: 'pdf', label: 'PDF' },
    { value: 'external_url', label: 'Link elsewhere' },
];

const KIND_LABELS: Record<ContestPolicyKind, string> = {
    contest_rules: 'Contest rules',
    privacy_policy: 'Privacy policy',
    terms_and_conditions: 'Terms and conditions',
    other: 'Other',
};

const KINDS = (Object.keys(KIND_LABELS) as ContestPolicyKind[]).map((kind) => ({
    value: kind,
    label: KIND_LABELS[kind],
}));

const STATUS_LABELS: Record<ContestPolicyVersionStatus, string> = {
    in_force: 'In force',
    scheduled: 'To come',
    replaced: 'Replaced',
};

interface PoliciesTabProps {
    formik: FormikProps<PoliciesFormValues>;
    /** Null while the contest is being created. */
    contest: Contest | null;
    /** The time zone the General tab holds, saved or not. */
    timeZone: string;
    /** Opens the preview on a policy's page, by its id in the preview. */
    onPreview: (policyId: number) => void;
}

/** Where the version in force stands, said once above each saved policy. */
function versionSummary(saved: ContestPolicy | undefined): string {
    const inForce = saved?.versions.find(
        (version) => version.status === 'in_force'
    );
    if (!saved?.content) {
        return 'Not written yet: saving its content publishes version 1.';
    }
    if (!inForce) {
        return `Version ${saved.content.version} is to come. No version is in force yet.`;
    }
    const participants = countParticipants(inForce.participantCount);
    return `Version ${
        inForce.version
    } is in force, accepted by ${participants}. Changing the content publishes version ${
        saved.content.version + 1
    }; they keep the version they accepted.`;
}

/**
 * The contest's legal pages, in the order the drawer lists them after its
 * pages. What names a policy changes in place; a change of what it says is
 * a new version, which participants accept from then on.
 */
export const PoliciesTab = observer(
    ({ formik, contest, timeZone, onPreview }: PoliciesTabProps) => {
        const store = useContestStore();
        const { values, setFieldValue } = formik;

        const errorOf = (path: string) =>
            formik.submitCount > 0
                ? (formik.getFieldMeta(path).error as string | undefined)
                : undefined;

        const savedOf = (form: PolicyForm) =>
            contest?.policies.find((policy) => policy.id === form.id);

        const move = (from: number, to: number) => {
            const policies = [...values.policies];
            const [moved] = policies.splice(from, 1);
            policies.splice(to, 0, moved);
            setFieldValue('policies', policies);
        };

        const remove = (index: number) => {
            setFieldValue(
                'policies',
                values.policies.filter((_, i) => i !== index)
            );
        };

        // A PDF is written at the place its policy had when the upload began,
        // so the policies keep their places until every upload is done.
        const isUploading = store.pendingUploads > 0;

        const uploadPdf = async (path: string, file: File) => {
            const url = await store.uploadDocument(file);
            if (url) {
                setFieldValue(path, url);
            }
        };

        const textInput = (
            path: string,
            label: string,
            options: {
                maxLength?: number;
                disabled?: boolean;
                helperText?: string;
                placeholder?: string;
            } = {}
        ) => (
            <LabeledField
                label={label}
                hasError={!!errorOf(path)}
                helperText={options.helperText}
            >
                <TextField
                    value={formik.getFieldMeta<string>(path).value}
                    maxLength={options.maxLength}
                    disabled={options.disabled}
                    placeholder={options.placeholder}
                    onChange={(e) => setFieldValue(path, e.target.value)}
                />
                <FieldError error={errorOf(path)} />
            </LabeledField>
        );

        const contentInput = (index: number, lang: Language) => {
            const path = `policies.${index}.translations.${lang}`;
            const form = values.policies[index].translations[lang];
            switch (form.contentType) {
                case 'html':
                    return (
                        <>
                            <RichText
                                withStructure
                                text={form.body}
                                onChange={(body) =>
                                    setFieldValue(`${path}.body`, body)
                                }
                            />
                            <FieldError error={errorOf(`${path}.body`)} />
                        </>
                    );
                case 'pdf':
                    return (
                        <>
                            {form.documentUrl && (
                                <a
                                    href={form.documentUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Open the PDF
                                </a>
                            )}
                            <ButtonFileInput
                                accept="application/pdf"
                                text={
                                    form.documentUrl
                                        ? 'Replace the PDF'
                                        : 'Upload a PDF'
                                }
                                isLoading={store.pendingUploads > 0}
                                onFileSelected={(file) =>
                                    uploadPdf(`${path}.documentUrl`, file)
                                }
                            />
                            <FieldError
                                error={errorOf(`${path}.documentUrl`)}
                            />
                        </>
                    );
                case 'external_url':
                    return textInput(`${path}.externalUrl`, 'Address', {
                        maxLength: 1024,
                    });
                default:
                    return null;
            }
        };

        const languageColumn = (index: number, lang: Language) => {
            const path = `policies.${index}.translations.${lang}`;
            const saved = savedOf(values.policies[index]);
            const isLocked =
                !!contest?.isActive &&
                !!saved?.translations.some((each) => each.lang === lang);
            return (
                <div key={lang} className={style.language}>
                    <div className="h6_bold">
                        {lang === 'en' ? 'English' : 'French'}
                    </div>
                    {textInput(`${path}.title`, 'Title', { maxLength: 200 })}
                    {textInput(`${path}.urlSegment`, 'Address', {
                        maxLength: 100,
                        disabled: isLocked,
                        helperText: isLocked
                            ? 'The contest is published: this address no longer changes.'
                            : 'The end of the policy link.',
                        placeholder:
                            'e.g. privacy-policy or terms-and-conditions',
                    })}
                    {textInput(`${path}.acceptanceLabel`, 'Sign-up checkbox', {
                        maxLength: 500,
                        helperText:
                            'Asks to accept the policy at sign-up. The text in brackets opens it: I accept the [contest rules]. Leave it empty when sign-up does not ask.',
                    })}
                    <LabeledField
                        label="Content"
                        hasError={!!errorOf(`${path}.contentType`)}
                    >
                        <Select
                            value={
                                values.policies[index].translations[lang]
                                    .contentType
                            }
                            onChange={(contentType) =>
                                setFieldValue(
                                    `${path}.contentType`,
                                    contentType
                                )
                            }
                            options={CONTENT_TYPES}
                            size="small"
                        />
                        <FieldError error={errorOf(`${path}.contentType`)} />
                    </LabeledField>
                    {contentInput(index, lang)}
                </div>
            );
        };

        const versionTable = (saved: ContestPolicy) => (
            <Table
                size="small"
                pagination={false}
                rowKey="version"
                dataSource={saved.versions}
                columns={[
                    { title: 'Version', dataIndex: 'version' },
                    {
                        title: 'Takes effect',
                        dataIndex: 'effectiveAt',
                        render: (value: string) => localDate(value, timeZone),
                    },
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        render: (status: ContestPolicyVersionStatus) =>
                            STATUS_LABELS[status],
                    },
                    { title: 'Participants', dataIndex: 'participantCount' },
                ]}
            />
        );

        return (
            <div className={style.policies_tab}>
                <p className="footnote_description">
                    The drawer lists these policies after the contest pages, in
                    this order. Publishing the contest takes its rules, written
                    in English and French, and a privacy policy.
                </p>
                {values.policies.map((form, index) => {
                    const saved = savedOf(form);
                    const accepted = saved?.versions.some(
                        (version) => version.participantCount > 0
                    );
                    return (
                        <Section
                            key={saved?.id ?? `new-${index}`}
                            title={
                                <span className={style.title}>
                                    {form.translations.en.title ||
                                        form.translations.fr.title ||
                                        'New policy'}
                                    {form.kind && (
                                        <Tag variant="greek-blue">
                                            {KIND_LABELS[form.kind]}
                                        </Tag>
                                    )}
                                </span>
                            }
                            headerActions={
                                <>
                                    <SquareButton
                                        ariaLabel="Preview the policy"
                                        icon={<EyeOutlined />}
                                        disabled={!contest}
                                        onClick={() =>
                                            onPreview(previewIdOf(form, index))
                                        }
                                    />
                                    <SquareButton
                                        ariaLabel="Move up"
                                        icon={<ArrowUpOutlined />}
                                        disabled={isUploading || index === 0}
                                        onClick={() => move(index, index - 1)}
                                    />
                                    <SquareButton
                                        ariaLabel="Move down"
                                        icon={<ArrowDownOutlined />}
                                        disabled={
                                            isUploading ||
                                            index === values.policies.length - 1
                                        }
                                        onClick={() => move(index, index + 1)}
                                    />
                                    <Tooltip
                                        title={
                                            accepted
                                                ? 'Participants accepted this policy, so it stays. Publish a new version instead.'
                                                : undefined
                                        }
                                    >
                                        {/* A disabled button fires no hover, so the span carries the tooltip. */}
                                        <span>
                                            <SquareButton
                                                ariaLabel="Delete the policy"
                                                icon={<DeleteOutlined />}
                                                disabled={
                                                    isUploading || accepted
                                                }
                                                onClick={() => remove(index)}
                                            />
                                        </span>
                                    </Tooltip>
                                </>
                            }
                        >
                            <p className="footnote_description">
                                {versionSummary(saved)}
                            </p>
                            <div className={style.row}>
                                <LabeledField
                                    label="Type"
                                    hasError={
                                        !!errorOf(`policies.${index}.kind`)
                                    }
                                >
                                    <Select
                                        value={form.kind ?? undefined}
                                        placeholder="Choose a type"
                                        onChange={(kind) =>
                                            setFieldValue(
                                                `policies.${index}.kind`,
                                                kind
                                            )
                                        }
                                        options={KINDS}
                                        size="small"
                                    />
                                    <FieldError
                                        error={errorOf(
                                            `policies.${index}.kind`
                                        )}
                                    />
                                </LabeledField>
                                <LabeledField
                                    label="Provinces"
                                    helperText="Empty applies everywhere the contest is open. A banner's provinces give it a policy of its own."
                                >
                                    <BusinessUnitsSelectTree
                                        value={form.provinceIds}
                                        onChange={(ids) =>
                                            setFieldValue(
                                                `policies.${index}.provinceIds`,
                                                cleanProvinceIds(ids)
                                            )
                                        }
                                    />
                                </LabeledField>
                            </div>
                            <div className={style.row}>
                                {LANGUAGES.map((lang) =>
                                    languageColumn(index, lang)
                                )}
                            </div>
                            <LabeledField
                                label="New version takes effect"
                                hasError={
                                    !!errorOf(`policies.${index}.effectiveAt`)
                                }
                                helperText={`In the contest's time zone, ${timeZone}. Leave it empty for the new version to take effect once saved, or on the date of a version still to come.`}
                            >
                                <DatePicker
                                    showTime={{ format: 'HH:mm' }}
                                    format="YYYY-MM-DD HH:mm"
                                    value={form.effectiveAt}
                                    onChange={(date) =>
                                        setFieldValue(
                                            `policies.${index}.effectiveAt`,
                                            date
                                        )
                                    }
                                    size="small"
                                />
                                <FieldError
                                    error={errorOf(
                                        `policies.${index}.effectiveAt`
                                    )}
                                />
                            </LabeledField>
                            {saved &&
                                saved.versions.length > 0 &&
                                versionTable(saved)}
                        </Section>
                    );
                })}
                <div>
                    <Button
                        variant="secondary"
                        onClick={() =>
                            setFieldValue('policies', [
                                ...values.policies,
                                newPolicyForm(),
                            ])
                        }
                    >
                        Add a policy
                    </Button>
                </div>
            </div>
        );
    }
);
